import { useEffect, useRef } from 'react';
import posthog from '../lib/posthog';
import { useStateContext } from '../contexts/ContextProvider';

export function usePostHogIdentify() {
    const { user, token, currentFacility } = useStateContext();
    const prevUserIdRef = useRef<number | null>(null);
    const prevFacilityIdRef = useRef<number | null>(null);

    // Identify user on login / app load
    useEffect(() => {
        if (!token || !user?.id || user.id === 0) {
            // Logout: reset PostHog
            if (prevUserIdRef.current !== null) {
                posthog.reset();
                prevUserIdRef.current = null;
                prevFacilityIdRef.current = null;
            }
            return;
        }

        // Identify only when user changes
        if (prevUserIdRef.current !== user.id) {
            posthog.identify(`user_${user.id}`, {
                email: user.email,
                name: [user.firstname, user.lastname].filter(Boolean).join(' '),
                user_type: user.is_super_admin ? 'super_admin' : user.is_admin ? 'admin' : 'user',
                is_admin: user.is_admin,
                facility_id: currentFacility?.id ?? user.facility_id,
                facility_name: currentFacility?.name ?? undefined,
            });
            prevUserIdRef.current = user.id;
        }
    }, [token, user?.id, user?.email, user?.is_admin, user?.is_super_admin]);

    // Group by facility + track switches
    useEffect(() => {
        if (!token || !currentFacility?.id) return;

        const switched = prevFacilityIdRef.current !== null
            && prevFacilityIdRef.current !== currentFacility.id;

        posthog.group('facility', `facility_${currentFacility.id}`, {
            name: currentFacility.name,
            timezone: currentFacility.timezone ?? undefined,
        });

        if (switched) {
            posthog.capture('facility_switched', {
                from_facility_id: prevFacilityIdRef.current,
                to_facility_id: currentFacility.id,
            });
        }

        prevFacilityIdRef.current = currentFacility.id;
    }, [token, currentFacility?.id, currentFacility?.name]);
}
